import React, { useState, useEffect, useRef } from 'react';
import { Users, ChevronDown } from 'lucide-react';
import './AgeFilter.css';

interface AgeFilterProps {
  onAgesChange: (ages: number[]) => void;
  initialAges?: number[];
}

const AgeFilter: React.FC<AgeFilterProps> = ({
  onAgesChange,
  initialAges = []
}) => {
  const [showAgePicker, setShowAgePicker] = useState(false);
  const [selectedAges, setSelectedAges] = useState<number[]>(initialAges);
  const agePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (agePickerRef.current && !agePickerRef.current.contains(event.target as Node)) {
        // Check if the click was on the button itself
        const container = agePickerRef.current?.parentElement;
        const button = container?.querySelector('.filter-button-age');
        if (button && button.contains(event.target as Node)) {
          return; // Don't close if clicking the button
        }
        setShowAgePicker(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize selectedAges from props
  useEffect(() => {
    setSelectedAges(initialAges);
  }, [initialAges]);

  const handleAgeToggle = (age: number) => {
    const newSelectedAges = selectedAges.includes(age)
      ? selectedAges.filter(a => a !== age)
      : [...selectedAges, age];
    
    setSelectedAges(newSelectedAges);
    onAgesChange(newSelectedAges);
  };

  const formatAgeRange = () => {
    if (selectedAges.length === 0) {
      return "Any Age";
    }
    if (selectedAges.length === 1) {
      return `Age ${selectedAges[0]}`;
    }
    if (selectedAges.length <= 3) {
      return `Ages ${selectedAges.sort((a, b) => a - b).join(', ')}`;
    }
    const sortedAges = selectedAges.sort((a, b) => a - b);
    return `Ages ${sortedAges.slice(0, 3).join(', ')}...`;
  };

  const handleClearAll = () => {
    setSelectedAges([]);
    onAgesChange([]);
  };

  return (
    <div className="filter-button-container">
      <button 
        className="filter-button filter-button-age"
        onClick={() => setShowAgePicker(!showAgePicker)}
      >
        <Users className="icon icon--sm" />
        <span>{formatAgeRange()}</span>
        <ChevronDown className="icon icon--sm filter-chevron" />
      </button>
      
      {showAgePicker && (
        <div className="age-picker-dropdown" ref={agePickerRef}>
          <div className="age-picker-header">
            <h4>Select Ages</h4>
            <button 
              className="age-picker-clear"
              onClick={handleClearAll}
            >
              Clear All
            </button>
          </div>
          <div className="age-picker-grid">
            {Array.from({ length: 18 }, (_, i) => i + 1).map(age => (
              <button
                key={age}
                className={`age-button ${selectedAges.includes(age) ? 'age-button--selected' : ''}`}
                onClick={() => handleAgeToggle(age)}
              >
                {age}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgeFilter;
